import React, { useEffect, useLayoutEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreakdownTabContext } from '../../contexts/BreakdownTabContext';
import { useBreakdownDataLoader } from '../../hooks/useBreakdownDataLoader';
import { useScheduleDataLoader } from '../../hooks/useScheduleDataLoader';
import ElementManager from '../ElementManager/ElementManager';

/** `#/elements/:breakdownId`：元素管理（与分场工作区数据引导一致）。 */
const ElementsWorkspace: React.FC = () => {
  const { breakdownId } = useParams<{ breakdownId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { setBreakdownId, switchTab } = useBreakdownTabContext();

  useLayoutEffect(() => {
    if (breakdownId) {
      setBreakdownId(breakdownId);
      switchTab('element-manager');
    }
  }, [breakdownId, setBreakdownId, switchTab]);

  const {
    currentScheduleId,
    loading: breakdownBootstrapLoading,
    error: breakdownBootstrapError,
  } = useBreakdownDataLoader({
    breakdownId: breakdownId || '',
    forceRefresh: false,
    preferredScheduleId: searchParams.get('schedule'),
  });

  useEffect(() => {
    if (!currentScheduleId || searchParams.get('schedule') === currentScheduleId) return;
    setSearchParams({ schedule: currentScheduleId }, { replace: true });
  }, [currentScheduleId, searchParams, setSearchParams]);

  const { schedule: scheduleInfo } = useScheduleDataLoader({
    scheduleId: currentScheduleId || '',
    forceRefresh: false,
  });

  useEffect(() => {
    if (!breakdownId) {
      navigate('/', { replace: true });
    }
  }, [breakdownId, navigate]);

  if (!breakdownId) {
    return null;
  }

  if (breakdownBootstrapLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 60,
          color: 'var(--color-gray2, #888)',
        }}
      >
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (breakdownBootstrapError) {
    return <div style={{ padding: 24, color: 'var(--error-color, #c00)' }}>{breakdownBootstrapError}</div>;
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ElementManager
        breakdownId={breakdownId}
        currentScheduleID={currentScheduleId ?? undefined}
        scheduleInfo={scheduleInfo}
      />
    </div>
  );
};

export default ElementsWorkspace;
